// ==================== ADD ORGANIZATION MODAL ====================
// Multiple organizations: add one of the WhatsApp accounts the customer has paid for.
// The business details (Settings → Organization) are the customer's own and shared by
// all their accounts, so an account is only given a name.

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, AlertCircle, Plus } from 'lucide-react';
import { Button, Modal } from '../ui';
import { organizationsService, type Organization } from '../../services/organizations.service';

// ==================== VALIDATION SCHEMA ====================

const accountSchema = z.object({
  businessName: z.string().min(2, 'Account name must be at least 2 characters').max(100),
});

type AccountFormData = z.infer<typeof accountSchema>;

const inputClassName = (hasError: boolean) =>
  `w-full pl-10 pr-4 py-2.5 text-sm bg-[#0D1117] border rounded-lg text-white placeholder-gray-500
   focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all
   ${hasError ? 'border-red-500' : 'border-gray-700 focus:border-orange-500'}`;

interface AddOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (organization: Organization) => void;
}

export const AddOrganizationModal = ({ isOpen, onClose, onCreated }: AddOrganizationModalProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: { businessName: '' },
  });

  // Start empty every time the modal opens
  useEffect(() => {
    if (isOpen) {
      reset({ businessName: '' });
      setError(null);
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: AccountFormData) => {
    try {
      setSaving(true);
      setError(null);
      onCreated(await organizationsService.create(data.businessName));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add WhatsApp account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title="Add WhatsApp Account"
      subtitle="Each account connects its own WhatsApp number"
      size="md"
      contentClassName="px-6 py-5"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1.5">
            Account Name *
          </label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="e.g., Acme Sales"
              {...register('businessName')}
              className={inputClassName(!!errors.businessName)}
            />
          </div>
          {errors.businessName && (
            <p className="text-xs text-red-400 mt-1">{errors.businessName.message}</p>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <p className="text-xs text-red-300">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-1">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
            isLoading={saving}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Add Account
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddOrganizationModal;
